import { OrderDirection } from '../../common/enums/order-direction.enum';
import { IPaginatedEntity } from '../../common/interfaces/paginated-entity.interface';
import { ClientStatus, IClient, IClientDTO } from '../../common/interfaces/client.interface';
import { supabase } from '../../common/config/supabase';
import { getClientCertificate } from '../certificate/certificate.api';
import { IClientCertificate } from '../../common/interfaces/client-certificate.interface';
import { getClientAnnex } from '../annex/annex.api';
import { IClientAnnex } from '../../common/interfaces/client-annex.interface';

export const getClients = async (
  companyId: number,
  limit: number,
  page: number,
  search?: string,
  orderBy?: string,
  orderDirection?: OrderDirection,
  status?: ClientStatus,
): Promise<IPaginatedEntity<IClient>> => {
  const { data, error } = await supabase.rpc('get_clients', {
    company_id_val: companyId,
    search_term: search || '',
    status_val: status,
    limit_val: limit,
    page_val: page,
    order_direction: orderDirection,
    order_by: orderBy,
  });

  if (error) {
    throw error;
  }

  return {
    items: data,
    meta: {
      currentPage: page,
      itemCount: data.length,
      itemsPerPage: limit,
      totalItems: data[0] ? data[0].total_count : 0,
      totalPages: data[0] ? Math.ceil(+data[0].total_count / limit) : 0,
      orderByColumn: orderBy,
      orderDirection: orderDirection,
    },
  };
};

export const getClient = async (id: string): Promise<IClient | null> => {
  const { data, error } = await supabase.from('clients').select('*').eq('id', id).single();

  const certificate: IClientCertificate = await getClientCertificate(id);
  const annex: IClientAnnex = await getClientAnnex(id);

  if (error) {
    throw error;
  }

  return {
    ...data,
    certificate,
    annex,
  };
};

export const addClient = async (client: IClientDTO): Promise<IClient | null> => {
  const { data, error } = await supabase.from('clients').insert({
    name: client.name,
    description: client.description,
    status: client.status,
    cui: client.cui,
    raf_number: client.raf_number,
    address: client.address,
    company_id: client.company_id,
  });

  if (error) {
    throw error;
  }

  return data;
};

export const updateClient = async (client: IClientDTO): Promise<IClient | null> => {
  if (!client.id) {
    return null;
  }

  const { data, error } = await supabase
    .from('clients')
    .update({
      name: client.name,
      description: client.description,
      status: client.status,
      cui: client.cui,
      raf_number: client.raf_number,
      address: client.address,
    })
    .eq('id', client.id);

  if (error) {
    throw error;
  }

  return data;
};
